import * as React from 'react';
import styled, { useTheme } from 'styled-components/macro';

import { getVideo } from 'lib/api/videos/useSingleVideoQuery';
import { NewModal } from 'lib/components';
import { VideoPlayer } from 'app/pages/video/videoPlayer';
import { LoadingIndicator } from 'lib/components';
import { usePlayerDesignQuery } from 'lib/api/playerDesign/usePlayerDesignQuery';
import { IoMdClose } from 'react-icons/io';
import { ParagraphSmall, ParagraphSmallBold } from '../styles/typography';
import { Flex } from '../styles/layout';
import { useTranslation } from 'react-i18next';

const ModalItem = styled.div`
  display: block;
  position: relative;
  background-color: ${({ theme }) => theme.colors.black[100]};
`;

const Content = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
`;
const CloseButtonWrap = styled.div`
  background-color: ${({ theme }) => theme.colors.black[100]};
  display: flex;
  justify-content: center;
  align-items: center;
  color: ${({ theme }) => theme.colors.white[100]};
  height: 24px;
  width: 24px;
  position: absolute;
  z-index: 10;
  right: 4px;
  top: 4px;
  border: 1px solid ${({ theme }) => theme.colors.white[100]};
  border-radius: 4px;
  &:hover {
    opacity: 0.7;
    cursor: pointer;
  }
`;

type Props = {
  handleModalClose: () => void;
  videoId: string;
  source?: string;
  hideContext?: boolean;
};

type VideoData = {
  id: string;
  title: string;
  dateCreated: string;
  views: string;
  autogeneratedThumbnail: string;
  autogeneratedGif: string;
  videoSource: string;
  personalThumbnail: string;
  playerBackgroundColor: string;
  playerIconsAndTextColor: string;
  playButtonPosition: string;
  wtvId?: number;
};

export const ModalVideoListVideo = ({
  videoId = '',
  source = '',
  hideContext = false,
  handleModalClose,
}: Props) => {
  const { t } = useTranslation();
  const { colors } = useTheme();
  const [isLoading, setIsLoading] = React.useState(false);
  const [isError, setIsError] = React.useState(false);
  const [data, setData] = React.useState<VideoData>();
  const [hideContextMenu, setHideContextMenu] = React.useState(hideContext);

  const { data: defaultAppearanceData } = usePlayerDesignQuery({ options: {} });

  React.useEffect(() => {
    if (videoId) {
      setIsLoading(true);
      getVideo(videoId)
        .then((video: any) => {
          setData(video);
          if (video.wtvId !== undefined && video.wtvId > 0) {
            setHideContextMenu(true);
          }
          setIsLoading(false);
        })
        .catch(error => {
          console.log(error);
          setIsError(true);
          setIsLoading(false);
        });
    }
  }, [videoId]);

  const videoRef = React.createRef<HTMLVideoElement>();

  if (isLoading) {
    return (
      <NewModal
        hideHeader={true}
        closeModal={handleModalClose}
        style={{
          content: { border: 'none', width: 720, height: 360, padding: 0 },
        }}
      >
        <div>
          <LoadingIndicator isLoading={true} />
        </div>
      </NewModal>
    );
  }

  return (
    <NewModal
      hideHeader={!isError}
      closeModal={handleModalClose}
      style={{
        content: isError
          ? { width: 720, height: 405, padding: 0, border: 'none' }
          : { width: 720, height: 405, padding: 0, border: 'none' },
      }}
    >
      {isError ? (
        <Flex justifyContent='center' alignItems='center' height='100%'>
          <ParagraphSmallBold color={colors.neutral[100]}>
            {t('Video preview not available!')}
          </ParagraphSmallBold>

          <ParagraphSmall style={{ color: colors.white[100] }}></ParagraphSmall>
        </Flex>
      ) : (
        <ModalItem>
          {(data || source !== '') && (
            <>
              <CloseButtonWrap
                title={'Close video preview'}
                onClick={handleModalClose}
              >
                <IoMdClose size={24} />
              </CloseButtonWrap>
              <Content>
                {(data && videoId && (
                  <VideoPlayer
                    playerBackgroundColor={data.playerBackgroundColor}
                    videoSource={data.videoSource}
                    videoId={data.id}
                    playButtonPosition={data.playButtonPosition}
                    playerIconsColor={data.playerIconsAndTextColor}
                    thumbnail={
                      data.personalThumbnail
                        ? data.personalThumbnail
                        : data.autogeneratedThumbnail
                    }
                    videoRef={videoRef}
                    autoplay={true}
                    hideContext={hideContextMenu}
                  />
                )) || (
                  <VideoPlayer
                    playerBackgroundColor={
                      defaultAppearanceData?.playerBackgroundColor
                    }
                    videoSource={source}
                    playButtonPosition={
                      defaultAppearanceData?.playButtonPosition
                    }
                    playerIconsColor={
                      defaultAppearanceData?.playerIconsAndTextColor
                    }
                    thumbnail={''}
                    videoRef={videoRef}
                    autoplay={true}
                    hideContext={hideContextMenu}
                  />
                )}
              </Content>
            </>
          )}
        </ModalItem>
      )}
    </NewModal>
  );
};
